import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { registerServiceWorker, subscribePush } from "@/lib/push";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import {
  LayoutDashboard, TrendingUp, Flame, Zap as ZapIcon, BarChart3,
  Newspaper, FileText, Bell, User, LogOut, Upload, Users, ClipboardList,
  Settings, Send, History, Menu, X, Sparkles, Activity, Shield, Leaf, Bot, Mail,
} from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_monitor-adybo/artifacts/tadicmii_Adybo%20Logo%20Colori%20Bianco%20R.png";

const clientNav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/prezzi", label: "Prezzi", icon: BarChart3, testid: "nav-prezzi" },
  { to: "/forward/ttf", label: "Forward Gas TTF", icon: Flame, testid: "nav-ttf" },
  { to: "/forward/psv", label: "Forward Gas PSV", icon: Flame, testid: "nav-psv" },
  { to: "/forward/power", label: "Forward Power", icon: ZapIcon, testid: "nav-power" },
  { to: "/forward/co2", label: "Forward CO2", icon: Leaf, testid: "nav-co2" },
  { to: "/psv-ttf-da-mid", label: "PSV - TTF DA Mid", icon: Flame, testid: "nav-spot-gas", spotGasOnly: true },
  { to: "/news", label: "News", icon: Newspaper, testid: "nav-news" },
  { to: "/briefing", label: "Report Mercati Energetici", icon: Sparkles, testid: "nav-briefing", marketReportsOnly: true },
  { to: "/report", label: "Analisi mensile e altri report", icon: FileText, testid: "nav-report" },
  { to: "/report-personalizzati", label: "Report Personalizzati", icon: FileText, testid: "nav-custom-reports", customReportsOnly: true },
  { to: "/notifiche", label: "Notifiche", icon: Bell, testid: "nav-notifiche" },
  { to: "/alert-prezzi", label: "Alert Prezzi", icon: Bell, testid: "nav-alert-prezzi", priceAlertsOnly: true },
  { to: "/profilo", label: "Profilo", icon: User, testid: "nav-profilo" },
];

const adminNav = [
  { to: "/admin/upload", label: "Caricamento Excel", icon: Upload, testid: "nav-admin-upload" },
  { to: "/admin/aggiornamento-mercato", label: "Caricamento Excel Automatico", icon: Activity, testid: "nav-admin-market-import" },
  { to: "/admin/uploads", label: "Storico Upload", icon: History, testid: "nav-admin-uploads" },
  { to: "/admin/news", label: "Gestione News", icon: Newspaper, testid: "nav-admin-news" },
  { to: "/admin/briefing", label: "Gestione Report Mercati Energetici", icon: Sparkles, testid: "nav-admin-briefing" },
  { to: "/admin/report", label: "Gestione Analisi e altri Report", icon: FileText, testid: "nav-admin-report" },
  { to: "/admin/report-personalizzati", label: "Gestione Report Personalizzati", icon: FileText, testid: "nav-admin-custom-reports" },
  { to: "/admin/energy-analytics", label: "Gestione Energy Analytics", icon: BarChart3, testid: "nav-admin-energy-analytics" },
  { to: "/admin/notifiche", label: "Notifiche", icon: Send, testid: "nav-admin-notifiche" },
  { to: "/admin/clienti", label: "Clienti", icon: Users, testid: "nav-admin-clienti" },
  { to: "/admin/gruppi-clienti", label: "Gruppi Clienti", icon: Users, testid: "nav-admin-gruppi-clienti" },
  { to: "/admin/storico-email", label: "Storico Email", icon: Mail, testid: "nav-admin-storico-email" },
  { to: "/admin/staff", label: "Gestione Staff", icon: Shield, testid: "nav-admin-staff", adminOnly: true },
  { to: "/admin/accessi", label: "Statistiche Accessi", icon: Activity, testid: "nav-admin-accessi" },
  { to: "/admin/alert-prezzi", label: "Gestione Alert Prezzi", icon: Bell, testid: "nav-admin-alert-prezzi" },
  { to: "/admin/ai", label: "Modelli AI", icon: Bot, testid: "nav-admin-ai", adminOnly: true },
  { to: "/admin/log", label: "Log Attività", icon: ClipboardList, testid: "nav-admin-log" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [unread, setUnread] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    (async () => {
      await registerServiceWorker();
      if (user?.notification_enabled) {
        try { await subscribePush(); } catch {}
      }
    })();
  }, [user?.id]);

  useEffect(() => {
    setMobileOpen(false);
  }, [loc.pathname]);

  const loadUnread = async () => {
    try {
      const { data } = await api.get("/notifications");
      setUnread(data.filter((n) => !n.is_read).length);
    } catch {
      // silent — 401/network errors non devono mai comparire come toast globale
    }
  };
  useEffect(() => {
    loadUnread();
    const id = setInterval(loadUnread, 30000);
    const handler = () => loadUnread();
    window.addEventListener("notifications:changed", handler);
    return () => {
      clearInterval(id);
      window.removeEventListener("notifications:changed", handler);
    };
  }, []);

  const isAdmin = user?.role === "admin";
  const isStaff = user?.role === "admin" || user?.role === "editor";
  const adminItems = adminNav.filter((i) => !i.adminOnly || isAdmin);
  // Nasconde "Report Personalizzati" nel menu cliente se non abilitato per lui.
  // Lo staff (admin/editor) vede sempre la voce per poter accedere all'area cliente.
  const clientItems = clientNav.filter(
    (i) => (
      (!i.customReportsOnly || isStaff || user?.custom_reports_enabled === true) &&
      (!i.spotGasOnly || isStaff || user?.spot_gas_enabled === true) &&
      (!i.priceAlertsOnly || isStaff || user?.price_alerts_enabled !== false) &&
      (!i.marketReportsOnly || isStaff || user?.market_reports_enabled !== false)
    ),
  );

  const currentLabel = [...clientNav, ...adminNav].find((n) => n.to === loc.pathname)?.label || "Adybo";

  return (
    <div className="min-h-screen bg-[#F6F8FC] flex">
      {/* Sidebar (desktop) */}
      <aside className={`hidden lg:flex flex-col w-64 bg-[#071B4D] text-white fixed inset-y-0 left-0 z-30`}>
        <SidebarContent
          isStaff={isStaff}
          adminItems={adminItems}
          clientItems={clientItems}
          user={user}
          logout={logout}
        />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-[#071B4D] text-white flex flex-col">
            <SidebarContent isStaff={isStaff} adminItems={adminItems} clientItems={clientItems} user={user} logout={logout} onClose={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 lg:pl-64 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="sticky top-0 z-20 h-16 bg-[#F6F8FC]/85 backdrop-blur-lg border-b border-slate-200 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2 rounded-lg hover:bg-slate-200" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
              <Menu size={22} />
            </button>
            <div className="font-display font-semibold text-lg text-slate-900">{currentLabel}</div>
          </div>
          <div className="flex items-center gap-3">
            <button
              className="relative p-2 rounded-lg hover:bg-slate-200"
              onClick={() => nav("/notifiche")}
              data-testid="topbar-bell"
              aria-label="Notifiche"
            >
              <Bell size={20} />
              {unread > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </button>
            <div className="hidden sm:flex items-center gap-2 pl-2 border-l border-slate-200">
              <div className="w-9 h-9 rounded-full brand-gradient flex items-center justify-center text-white text-sm font-bold">
                {user?.full_name?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="text-xs">
                <div className="font-semibold text-slate-900">{user?.full_name}</div>
                <div className="text-slate-500">{user?.company_name || user?.role}</div>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8 pb-24 lg:pb-8">
          <div className="max-w-[1600px] mx-auto fade-up">
            <Outlet />
          </div>
        </main>

        {/* Bottom nav (mobile) */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 z-30 flex justify-around py-2 shadow-lg">
          {[
            { to: "/", icon: LayoutDashboard, label: "Home" },
            { to: "/prezzi", icon: BarChart3, label: "Prezzi" },
            { to: "/news", icon: Newspaper, label: "News" },
            { to: "/report", icon: FileText, label: "Analisi" },
            { to: "/notifiche", icon: Bell, label: "Notifiche" },
          ].map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg text-[10px] ${
                  isActive ? "text-blue-600" : "text-slate-500"
                }`
              }
              data-testid={`bottom-nav-${it.label.toLowerCase()}`}
            >
              <it.icon size={20} />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}

function SidebarContent({ isStaff, adminItems, clientItems, user, logout, onClose }) {
  const items = clientItems || clientNav;
  return (
    <>
      <div className="h-20 flex items-center justify-between px-5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <img src={LOGO} alt="Adybo" className="h-9" />
          <div className="text-[10px] tracking-[0.2em] text-white/60 font-semibold">ENERGY MONITOR</div>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10">
            <X size={18} />
          </button>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        <div className="text-[10px] uppercase tracking-widest text-white/40 px-3 mb-2 mt-1">Area Cliente</div>
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.to === "/"}
            className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}
            data-testid={it.testid}
          >
            <it.icon size={17} />
            {it.label}
          </NavLink>
        ))}
        {isStaff && (
          <>
            <div className="text-[10px] uppercase tracking-widest text-white/40 px-3 mb-2 mt-6">Backoffice</div>
            {adminItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}
                data-testid={it.testid}
              >
                <it.icon size={17} />
                {it.label}
              </NavLink>
            ))}
          </>
        )}
      </nav>
      <div className="p-4 border-t border-white/10">
        <button
          onClick={logout}
          className="sidebar-item w-full justify-start"
          data-testid="logout-btn"
        >
          <LogOut size={17} /> Esci
        </button>
      </div>
    </>
  );
}
